
import { api } from './base';
import { cachedFetch, invalidateCached } from '@/shared/lib/refCache';

export interface SupportGroup {
  id: string;
  key: string;
  name: string;
  publicName?: string | null;
  description: string | null;
  membersCount?: number;
  calendarId?: string | null;
  sprintsEnabled?: boolean;
  taskPrefix?: string | null;
  sprintGraceDays?: number;
  isLead?: boolean;
  isPrimary?: boolean;
  archivedAt?: string | null;
  createdAt?: string;
}

export interface SupportGroupMember {
  isPrimary?: boolean;
  isLead?: boolean;
  id: string;
  groupId: string;
  userId: string;
  source: 'manual' | 'keycloak';
  createdAt?: string;
  user?: {
    id: string;
    username: string | null;
    email: string | null;
    displayName: string;
  } | null;
}

export interface SupportGroupKcMapping {
  id: string;
  groupId: string;
  kcGroup: string;
}

function invalidateGroups<T>(res: T): T {
  invalidateCached('support-groups:');
  return res;
}

export const supportGroupsApi = {
  list: () => api.get<SupportGroup[]>('/api/v1/admin/support-groups'),
  listBrief: () =>
    cachedFetch('support-groups:brief', 5 * 60_000, () =>
      api.get<SupportGroup[]>('/api/v1/support-groups'),
    ),
  listMine: () =>
    cachedFetch('support-groups:mine', 5 * 60_000, () =>
      api.get<SupportGroup[]>('/api/v1/support-groups/my'),
    ),
  listMembersBrief: (id: string) =>
    api
      .get<{ id: string; displayName: string; isLead: boolean }[]>(
        `/api/v1/support-groups/${id}/members`,
      )
      .then((r) => r.data),
  create: (input: {
    key: string;
    name: string;
    publicName?: string | null;
    description?: string | null;
    taskPrefix?: string | null;
  }) =>
    api.post<SupportGroup>('/api/v1/admin/support-groups', input).then(invalidateGroups),
  update: (
    id: string,
    input: {
      name?: string;
      publicName?: string | null;
      description?: string | null;
      sprintsEnabled?: boolean;
      sprintGraceDays?: number;
      taskPrefix?: string | null;
    },
  ) =>
    api.patch<SupportGroup>(`/api/v1/admin/support-groups/${id}`, input).then(invalidateGroups),
  remove: (id: string) => api.delete<unknown>(`/api/v1/admin/support-groups/${id}`).then(invalidateGroups),

  setArchived: (id: string, archived: boolean) =>
    api
      .put<{ ok: true }>(`/api/v1/admin/support-groups/${id}/archived`, { archived })
      .then(invalidateGroups),

  listMembers: (id: string) =>
    api.get<SupportGroupMember[]>(`/api/v1/admin/support-groups/${id}/members`),
  addMember: (id: string, userId: string) =>
    api
      .post<SupportGroupMember>(`/api/v1/admin/support-groups/${id}/members`, { userId })
      .then(invalidateGroups),
  removeMember: (id: string, userId: string) =>
    api
      .delete<unknown>(`/api/v1/admin/support-groups/${id}/members/${encodeURIComponent(userId)}`)
      .then(invalidateGroups),

  setLead: (id: string, userId: string, isLead: boolean) =>
    api
      .put<{ ok: true }>(
        `/api/v1/admin/support-groups/${id}/members/${encodeURIComponent(userId)}/lead`,
        { isLead },
      )
      .then(invalidateGroups),

  setPrimary: (id: string, userId: string, primary: boolean) =>
    api
      .put<{ ok: true }>(
        `/api/v1/admin/support-groups/${id}/members/${encodeURIComponent(userId)}/primary`,
        { primary },
      )
      .then(invalidateGroups),

  listClients: (id: string) => api.get<string[]>(`/api/v1/admin/support-groups/${id}/clients`),
  setClients: (id: string, clientIds: string[]) =>
    api.put<{ count: number }>(`/api/v1/admin/support-groups/${id}/clients`, { clientIds }),

  listKcMappings: (id: string) =>
    api.get<SupportGroupKcMapping[]>(`/api/v1/admin/support-groups/${id}/kc-mappings`),
  addKcMapping: (id: string, kcGroup: string) =>
    api.post<SupportGroupKcMapping>(`/api/v1/admin/support-groups/${id}/kc-mappings`, { kcGroup }),
  removeKcMapping: (id: string, kcGroup: string) =>
    api.delete<unknown>(
      `/api/v1/admin/support-groups/${id}/kc-mappings?kcGroup=${encodeURIComponent(kcGroup)}`,
    ),
};
